import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import TierBadge from "@/components/TierBadge";
import api from "@/lib/api";
import { CATEGORIES } from "@/pages/ClientCategoriesPage";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, MapPin, HardHat, X, ShieldCheck } from "@phosphor-icons/react";

const Avatar = ({ user, size = 48 }) => {
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name || "avatar"}
        style={{ width: size, height: size }}
        className="rounded-full object-cover"
      />
    );
  }
  const initials = (user?.name || user?.contractor_profile?.business_name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      style={{ width: size, height: size }}
      className="flex items-center justify-center rounded-full bg-[#0A0F0D] font-display text-sm font-bold text-white"
    >
      {initials}
    </div>
  );
};

const ContractorSearchPage = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const parts = new URLSearchParams();
        if (category && category !== "all") parts.set("category", category);
        if (q) parts.set("q", q);
        const { data } = await api.get(`/contractors/search?${parts.toString()}`);
        setContractors(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, q]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (category !== "all") next.set("category", category);
    if (q) next.set("q", q);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="contractor-search-page">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12 md:px-10 md:py-16">
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
            Find a Pro
          </p>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl">
            Ranked contractors near you.
          </h1>
          <p className="mt-3 text-base leading-relaxed text-[#5C605E]">
            Browse local pros ordered by reliability tier and completed jobs — then post a job to get quotes.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-10 grid gap-3 rounded-md border border-[#E5E2DB] bg-white p-4 md:grid-cols-[1fr_220px_auto]"
          data-testid="contractor-search-form"
        >
          <div className="relative">
            <MagnifyingGlass
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C605E]"
            />
            <Input
              placeholder="Search by name, area or bio"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="contractor-search-input"
              className="border-[#E5E2DB] bg-white pl-9 focus-visible:ring-[#C65D47]"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              className="border-[#E5E2DB] bg-white"
              data-testid="contractor-search-category-select"
            >
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="submit"
            data-testid="contractor-search-submit"
            className="bg-[#0A0F0D] text-white hover:bg-[#C65D47]"
          >
            Search
          </Button>
        </form>

        <div className="mt-10 grid gap-4 md:grid-cols-2" data-testid="contractor-search-results">
          {loading ? (
            <p className="text-[#5C605E]">Loading…</p>
          ) : contractors.length === 0 ? (
            <div className="col-span-full rounded-md border border-dashed border-[#D6D3CD] bg-white p-12 text-center">
              <HardHat size={40} weight="duotone" color="#5C605E" className="mx-auto" />
              <p className="mt-3 font-display text-lg font-semibold text-[#0A0F0D]">
                No contractors found
              </p>
              <p className="mt-1 text-sm text-[#5C605E]">
                Try a different category or clear your search.
              </p>
            </div>
          ) : (
            contractors.map((c) => {
              const p = c.contractor_profile || {};
              const cat = CATEGORIES.find((x) => x.id === p.category);
              return (
                <div
                  key={c.id}
                  className="flex items-start gap-4 rounded-md border border-[#E5E2DB] bg-white p-5 transition-all hover:border-[#0A0F0D] hover:shadow-brutal"
                  data-testid={`contractor-card-${c.id}`}
                >
                  <Avatar user={c} size={56} />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-lg font-bold tracking-tight text-[#0A0F0D]">
                        {p.business_name || c.name}
                      </h3>
                      <TierBadge tier={p.tier || "none"} size="sm" />
                      {p.id_verification_status === "verified" && (
                        <span
                          className="inline-flex items-center gap-1 rounded-sm bg-[#0A0F0D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white"
                          data-testid={`verified-id-badge-${c.id}`}
                          title="Identity verified by ConnectAPro"
                        >
                          <ShieldCheck size={10} weight="fill" /> Verified ID
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-[#5C605E]">
                      <span className="rounded-sm bg-[#0A0F0D] px-1.5 py-0.5 font-semibold uppercase tracking-widest text-white">
                        {cat?.label || p.category}
                      </span>
                      {p.completed_jobs ? ` · ${p.completed_jobs} jobs completed` : ""}
                      {p.years_experience ? ` · ${p.years_experience} yrs exp.` : ""}
                    </p>
                    {p.bio && (
                      <p className="mt-2 line-clamp-2 text-sm text-[#0A0F0D]">{p.bio}</p>
                    )}
                    {p.service_area && (
                      <p className="mt-2 inline-flex items-center gap-1 text-xs text-[#5C605E]">
                        <MapPin size={12} weight="bold" /> {p.service_area}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-16 rounded-md border border-[#0A0F0D] bg-[#0A0F0D] p-8 text-center text-white">
          <h2 className="font-display text-2xl font-bold">Need work done?</h2>
          <p className="mt-2 text-sm text-white/80">
            Post a job in seconds and let vetted pros quote you.
          </p>
          <Button
            onClick={() => navigate("/client/categories")}
            data-testid="search-cta-post-job"
            className="mt-4 bg-[#C65D47] text-white hover:bg-white hover:text-[#0A0F0D]"
          >
            Post a job
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ContractorSearchPage;
